import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { Service, SortOrderDirection } from 'webapp-types';
import { MetricLibrarySortBy, OrderDirection } from 'webapp/enums/enums';

import type { FilterSortSettings, ItemTypeBase } from 'webapp/models-custom/filter-sort/base';
import FilterSort from 'webapp/models-custom/filter-sort/base';

export default abstract class FilterSortMetricLibraryMainBase<
	T extends ItemTypeBase,
> extends FilterSort<T> {
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('constants') declare constantsService: Service.Constants;

	@tracked override sortProp = 'usedCount';
	@tracked override sortOrder: SortOrderDirection = OrderDirection.DESC;

	// will be overriden
	sortByPopularityValue = '';
	sortByAlphabeticallyValue = '';

	constructor(options: FilterSortSettings<T>) {
		super(options);

		const selectedFilters = this.readSavedSelectedFilters();
		if (selectedFilters[3]) {
			const selected = selectedFilters[3][0] as MetricLibrarySortBy;
			this.onSortFilterChange(selected);
		}

		// Category
		this.createFilter<string>({
			id: 1,
			label: 'Category',
			name: 'category',
			selected: selectedFilters[1] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.categoryOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((categoryId) => {
					return this.categoryFilterFunction(item, categoryId);
				});
			},
		});

		// Data source (used only)
		this.createFilter<string>({
			id: 2,
			label: 'Data source',
			name: 'data source',
			selected: selectedFilters[2] as string | undefined,
			stringValues: true,
			isMultiSelect: true,
			getOptions: () => {
				return this.datasourceOptions;
			},
			filterFn: (item, selected) => {
				return selected.some((sourceTypeKey) => {
					return this.dataSourceFilterFunction(item, sourceTypeKey);
				});
			},
		});

		// Sort by
		this.createFilter<MetricLibrarySortBy>({
			id: 3,
			label: 'Sort by',
			name: 'sort',
			selected: selectedFilters[3] as MetricLibrarySortBy | undefined,
			stringValues: true,
			isSort: true,
			getOptions: () => {
				return this.sortByOptions;
			},
			onSelectedChange: (filter) => {
				const selected = filter.selected[0]!;
				this.onSortFilterChange(selected);
			},
		});
	}

	get sortByOptions() {
		return Object.values(MetricLibrarySortBy).map((sort) => ({
			id: sort,
			text: sort,
		}));
	}

	get categoryOptions() {
		return this.mmSourceTypesService.getSourceTypeCategories();
	}

	@cached
	get datasourceOptions() {
		const list = this.mmSourceTypesService.getSourceTypesList();

		list.sort((a, b) => {
			return a.text.localeCompare(b.text);
		});

		list.unshift({ id: 'all', text: 'All' });

		return list;
	}

	// will be overidden
	categoryFilterFunction(_item: T, _selected: string) {
		return true;
	}

	// will be overidden
	dataSourceFilterFunction(_item: T, _selected: string) {
		return true;
	}

	// Might be overriden
	override updateSortBy(sortBy: MetricLibrarySortBy): void {
		switch (sortBy) {
			default:
			case MetricLibrarySortBy.POPULARITY:
				this.sortProp = this.sortByPopularityValue;
				this.sortOrder = OrderDirection.DESC;
				break;
			case MetricLibrarySortBy.ALPHABETICALLY: {
				this.sortProp = this.sortByAlphabeticallyValue;
				this.sortOrder = OrderDirection.ASC;
				break;
			}
		}
	}
}
